/**
 * The clearance figures the public pages are allowed to print.
 *
 * WHY THIS FILE EXISTS. Three published tables disagreed with each other and
 * with the product, on pages that link to one another:
 *
 *   material          /slitting-machine  /slitting-problems  /slitting-calculator  solver
 *   carbon steel      8 to 10%           8 to 12%            8 to 12%              8 to 10%
 *   stainless         10 to 15%          10 to 15%           10 to 15%             10 to 14%
 *   aluminium         5 to 7%            10 to 15%           not shown             5 to 6%
 *   galvanised        8 to 12%           8 to 12%            not shown             10 to 11%
 *   AHSS / HSLA       12 to 18%          6 to 10%            not shown             not carried
 *
 * Aluminium was out by a factor of two and AHSS ran in opposite directions on
 * two pages that cross-link. A reader who follows a link from one to the other
 * is entitled to the same number twice.
 *
 * So the pages no longer carry their own numbers. They render from
 * PUBLISHED_CLEARANCE_BANDS below, which is DERIVED from the per-material
 * percentages rather than typed alongside them, and a page cannot print a
 * figure that is not in here.
 *
 * WHERE THE NUMBERS COME FROM. MATERIAL_CLEARANCE_PCT mirrors the table the
 * product actually ships, in
 * src/app/[locale]/(app)/jobs/new/Step1Client.tsx. That file is a route behind
 * login and outside the scope of this work, so it is not imported here and it
 * has not been touched; the copy is kept honest by
 * scripts/check-clearance-consistency.mjs (pnpm check:clearance), which parses
 * both and fails on any difference. Run it before changing either.
 *
 * HOW IT SQUARES WITH MAXWELL'S PUBLISHED CHART. Their chart is organised by
 * tensile band rather than by material name, which is the whole point of the
 * sentence every page now carries:
 *
 *   up to about 150 MPa    dead soft aluminium, copper, brass          3 to 6%
 *   about 150 to 350 MPa   CQ and DDQ cold rolled, galvanised, tin     6 to 10%
 *   about 350 to 620 MPa   hot rolled, annealed stainless              10 to 14%
 *   about 620 to 1350 MPa  HSLA and AHSS, full hard stainless          14 to 25%
 *
 * Every material below falls inside its band. Three do not line up exactly, and
 * those are listed for the Maxwell team in seo/2026-09/REPORT.md rather than
 * quietly reconciled here: pre-painted galvanised (we ship 11%, their
 * galvanised band tops at 10%), brass (we ship 7%, their non-ferrous band tops
 * at 6%), and electrical steel, which their chart does not name at all.
 */

/**
 * Per-material horizontal clearance, percent of thickness PER SIDE.
 *
 * Mirror of MATERIAL_CLEARANCE_PCT in Step1Client.tsx. Do not edit one without
 * the other; pnpm check:clearance fails the build if they diverge.
 */
export const MATERIAL_CLEARANCE_PCT: Readonly<Record<string, number>> = {
  "Cold Rolled Steel (CR)": 8,
  "Hot Rolled Steel (HR)": 10,
  "Galvanized Steel (GI / Z)": 10,
  "Pre-Painted Galvanized (PPGI)": 11,
  "Galvalume (Al-Zn Coated)": 10,
  "Stainless Steel 304 / 304L": 12,
  "Stainless Steel 316 / 316L": 14,
  "Stainless Steel 430": 10,
  "Electrical Steel CRGO": 6,
  "Electrical Steel CRNGO": 7,
  "Aluminum (1xxx / 3xxx)": 5,
  "Aluminum (5xxx / 6xxx)": 6,
  "Copper (ETP / OFC)": 6,
  "Brass (CuZn30 / CuZn37)": 7,
  "Tinplate (Tin-Coated Steel)": 6,
} as const;

/** Which shipped materials roll up into each row a public page prints. */
const BAND_MEMBERS: ReadonlyArray<{
  label: string;
  note: string;
  members: readonly string[];
}> = [
  {
    label: "Cold rolled steel",
    note: "The baseline most service centres work from",
    members: ["Cold Rolled Steel (CR)"],
  },
  {
    label: "Hot rolled steel",
    note: "Heavier and less uniform than CR, so it takes a wider gap",
    members: ["Hot Rolled Steel (HR)"],
  },
  {
    label: "Galvanised and Galvalume",
    note: "Pre-painted sits at the top of the range to protect the coating",
    members: [
      "Galvanized Steel (GI / Z)",
      "Pre-Painted Galvanized (PPGI)",
      "Galvalume (Al-Zn Coated)",
    ],
  },
  {
    label: "Stainless steel",
    note: "430 at the bottom, 304 in the middle, 316 at the top",
    members: [
      "Stainless Steel 430",
      "Stainless Steel 304 / 304L",
      "Stainless Steel 316 / 316L",
    ],
  },
  {
    label: "Electrical steel (CRGO and CRNGO)",
    note: "Thin and abrasive; the burr limit usually decides this, not the chart",
    members: ["Electrical Steel CRGO", "Electrical Steel CRNGO"],
  },
  {
    label: "Aluminium",
    note: "1xxx and 3xxx at the bottom, 5xxx and 6xxx at the top",
    members: ["Aluminum (1xxx / 3xxx)", "Aluminum (5xxx / 6xxx)"],
  },
  {
    label: "Copper and brass",
    note: "Ductile, so the gap stays tight or the edge tears rather than shears",
    members: ["Copper (ETP / OFC)", "Brass (CuZn30 / CuZn37)"],
  },
  {
    label: "Tinplate",
    note: "Treated as CQ steel for clearance",
    members: ["Tinplate (Tin-Coated Steel)"],
  },
];

export type ClearanceBand = {
  /** Material family as a reader would name it. */
  material: string;
  /** Rendered range, e.g. "10 to 14%" or "8%". */
  range: string;
  note: string;
  /** Present only where the figure is not one the product ships. */
  source?: string;
};

function bandFor(members: readonly string[]): string {
  const values = members.map((m) => {
    const v = MATERIAL_CLEARANCE_PCT[m];
    if (v == null) {
      throw new Error(
        `materialClearancePct: "${m}" is not in MATERIAL_CLEARANCE_PCT. ` +
          `A published clearance band cannot reference a material the product does not ship.`
      );
    }
    return v;
  });
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return lo === hi ? `${lo}%` : `${lo} to ${hi}%`;
}

/**
 * The rows a public page prints. Derived, never typed by hand.
 *
 * AHSS is the one row whose figure the product does not carry: there is no AHSS
 * baseline in MATERIAL_CLEARANCE_PCT, only an operator override that accepts up
 * to 25%. It is attributed rather than invented, which is also how the two
 * contradictory numbers that used to sit on our pages got resolved.
 */
export const PUBLISHED_CLEARANCE_BANDS: readonly ClearanceBand[] = [
  ...BAND_MEMBERS.map(({ label, note, members }) => ({
    material: label,
    range: bandFor(members),
    note,
  })),
  {
    material: "HSLA and advanced high strength steel",
    range: "14 to 25%",
    note: "Rises steeply with tensile; verify on a test cut before a production run",
    source: "maxwell-chart",
  },
];

/**
 * The three material buttons the free calculator offers, mapped to the shipped
 * material whose percentage they should default to.
 *
 * The calculator used to carry its own literals: carbon 10, stainless 12,
 * aluminium 8. All three disagreed with the solver AND with the prose further
 * down the same page. Aluminium at 8% was the worst, against 5% shipped, on a
 * page that also printed "5 to 6% for aluminium" two sections later.
 *
 * Grades are named in the helper text because a bare "stainless 12" is not
 * true of all stainless: 430 takes 10 and 316 takes 14. The calculator offers
 * one number per family, so it should say which grade that number is.
 */
export const CALCULATOR_CLEARANCE_MATERIALS = [
  { key: "carbon", label: "Carbon steel", material: "Cold Rolled Steel (CR)", grade: "cold rolled steel" },
  { key: "stainless", label: "Stainless steel", material: "Stainless Steel 304 / 304L", grade: "304 stainless" },
  { key: "aluminium", label: "Aluminium", material: "Aluminum (1xxx / 3xxx)", grade: "1xxx and 3xxx aluminium" },
] as const;

export type CalculatorClearanceKey =
  (typeof CALCULATOR_CLEARANCE_MATERIALS)[number]["key"];

/** Default clearance percentage per calculator material, as a string for the input. */
export const CALCULATOR_CLEARANCE_DEFAULTS: Record<CalculatorClearanceKey, string> =
  Object.fromEntries(
    CALCULATOR_CLEARANCE_MATERIALS.map((m) => {
      const pct = MATERIAL_CLEARANCE_PCT[m.material];
      if (pct == null) {
        throw new Error(
          `CALCULATOR_CLEARANCE_DEFAULTS: "${m.material}" is not a shipped material.`
        );
      }
      return [m.key, String(pct)];
    })
  ) as Record<CalculatorClearanceKey, string>;

/** "cold rolled steel 8%, 304 stainless 12%, 1xxx and 3xxx aluminium 5%" */
export const CALCULATOR_CLEARANCE_HELPER_TEXT = CALCULATOR_CLEARANCE_MATERIALS.map(
  (m) => `${m.grade} ${MATERIAL_CLEARANCE_PCT[m.material]}%`
).join(", ");

/** The sentence that has to sit above any published clearance table. */
export const CLEARANCE_TENSILE_NOTE =
  "These percentages are shorthand for typical tensile bands rather than properties of the metal itself, so the same alloy in a hard temper and a soft temper will not take the same figure. Treat them as a starting point and confirm with a test cut.";

export const MAXWELL_CLEARANCE_CHART_URL =
  "https://www.maxwellslitters.com/slitter-knife-clearance-chart/";

/**
 * Build-time guard, called from src/app/sitemap.ts alongside the sitemap and
 * enquiry assertions so it runs on every build and fails it loudly.
 *
 * Catches the two ways this can rot: a band row that names a material the
 * product no longer ships (bandFor throws), and a row carrying a figure that is
 * neither derived from the constant nor attributed to a source.
 */
export function assertClearanceBandsDerived(): void {
  // The free calculator's per-material defaults are part of the same contract:
  // they were hand typed and all three disagreed with the solver.
  for (const m of CALCULATOR_CLEARANCE_MATERIALS) {
    const shipped = MATERIAL_CLEARANCE_PCT[m.material];
    if (shipped == null) {
      throw new Error(
        `assertClearanceBandsDerived: calculator material "${m.material}" is not shipped.`
      );
    }
    if (CALCULATOR_CLEARANCE_DEFAULTS[m.key] !== String(shipped)) {
      throw new Error(
        `assertClearanceBandsDerived: calculator default for ${m.key} is ` +
          `${CALCULATOR_CLEARANCE_DEFAULTS[m.key]} but the product ships ${shipped}.`
      );
    }
  }

  for (const band of PUBLISHED_CLEARANCE_BANDS) {
    if (band.source) continue;
    const members = BAND_MEMBERS.find((b) => b.label === band.material)?.members;
    if (!members) {
      throw new Error(
        `assertClearanceBandsDerived: published band "${band.material}" has no ` +
          `member list and no source. Every printed clearance figure must come ` +
          `from MATERIAL_CLEARANCE_PCT or name where it came from.`
      );
    }
    const expected = bandFor(members);
    if (band.range !== expected) {
      throw new Error(
        `assertClearanceBandsDerived: "${band.material}" prints ${band.range} ` +
          `but the shipped materials give ${expected}.`
      );
    }
  }
}
